import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Clock, Save, Search, Users, Calendar, Loader2, AlertTriangle, 
  Plus, RefreshCw, Settings, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

/**
 * Trial Settings Tab - Manage global trial configuration and individual vendor trials
 */
const TrialSettingsTab = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  
  // Global config state
  const [globalConfig, setGlobalConfig] = useState({
    default_trial_days: 14,
    trial_extension_days: 7,
    trial_extension_offer_text: ''
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  
  // Vendor trial management state
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extensionDays, setExtensionDays] = useState(7);
  const [extensionReason, setExtensionReason] = useState('');
  const [extending, setExtending] = useState(false);

  // multi-select for bulk extend
  const [checkedVendors, setCheckedVendors] = useState(new Set());

  useEffect(() => {
    fetchGlobalConfig();
    loadTrialVendors('');
  }, []);

  const fetchGlobalConfig = async () => {
    try {
      setConfigLoading(true);
      const data = await apiCall('/api/trial-config/');
      setGlobalConfig(data);
    } catch (error) {
      console.error('Error fetching trial config:', error);
      // Set defaults if fetch fails
      setGlobalConfig({
        default_trial_days: 14,
        trial_extension_days: 7,
        trial_extension_offer_text: ''
      });
    } finally {
      setConfigLoading(false);
    }
  };

  const saveGlobalConfig = async () => {
    try {
      setConfigSaving(true);
      await apiCall('/api/trial-config/', {
        method: 'PUT',
        body: JSON.stringify(globalConfig)
      });
      toast({ title: 'Success', description: 'Trial configuration saved' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setConfigSaving(false);
    }
  };

  const loadTrialVendors = async (q) => {
    try {
      setVendorLoading(true);
      const data = await apiCall(`/api/custom-plans/vendors/search?q=${encodeURIComponent(q)}&status_filter=trial&limit=50`);
      setVendors(data.vendors || []);
    } catch (error) {
      console.error('Error fetching trial vendors:', error);
      setVendors([]);
    } finally {
      setVendorLoading(false);
    }
  };

  const searchVendors = useCallback(async () => {
    loadTrialVendors(vendorSearch);
  }, [vendorSearch]);

  useEffect(() => {
    const timeout = setTimeout(searchVendors, 300);
    return () => clearTimeout(timeout);
  }, [vendorSearch, searchVendors]);

  const fetchVendorTrialStatus = async (vendorId) => {
    try {
      const data = await apiCall(`/api/custom-plans/vendors/${vendorId}/trial-status`);
      setTrialStatus(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch trial status',
        variant: 'destructive'
      });
    }
  };

  const handleExtendTrial = async () => {
    const targetIds = checkedVendors.size > 0
      ? Array.from(checkedVendors)
      : selectedVendor ? [selectedVendor.id] : [];

    if (!targetIds.length) return;

    try {
      setExtending(true);
      for (const vid of targetIds) {
        await apiCall(
          `/api/custom-plans/vendors/${vid}/extend-trial?extension_days=${extensionDays}&reason=${encodeURIComponent(extensionReason)}`,
          { method: 'POST' }
        );
      }
      toast({
        title: 'Success',
        description: targetIds.length > 1
          ? `Trial extended by ${extensionDays} days for ${targetIds.length} vendors`
          : `Trial extended by ${extensionDays} days`
      });
      setShowExtendDialog(false);
      setExtensionDays(7);
      setExtensionReason('');
      setCheckedVendors(new Set());
      if (selectedVendor && targetIds.includes(selectedVendor.id)) {
        fetchVendorTrialStatus(selectedVendor.id);
      }
      loadTrialVendors(vendorSearch);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to extend trial', variant: 'destructive' });
    } finally {
      setExtending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Global Trial Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Global Trial Configuration
          </CardTitle>
          <CardDescription>
            Default trial settings applied to all new vendors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {configLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Default Trial Days</Label>
                <Input
                  type="number"
                  value={globalConfig.default_trial_days}
                  onChange={e => setGlobalConfig({
                    ...globalConfig,
                    default_trial_days: parseInt(e.target.value) || 0
                  })}
                  data-testid="default-trial-days-input"
                />
                <p className="text-xs text-gray-500">
                  Number of days for new vendor trials
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Auto-Extension Days</Label>
                <Input
                  type="number"
                  value={globalConfig.trial_extension_days}
                  onChange={e => setGlobalConfig({
                    ...globalConfig,
                    trial_extension_days: parseInt(e.target.value) || 0
                  })}
                  data-testid="extension-days-input"
                />
                <p className="text-xs text-gray-500">
                  Days offered when trial is about to expire (0 = disabled)
                </p>
              </div>
              
              <div className="space-y-2 md:col-span-1">
                <Label>Extension Offer Message</Label>
                <Textarea
                  value={globalConfig.trial_extension_offer_text || ''}
                  onChange={e => setGlobalConfig({
                    ...globalConfig,
                    trial_extension_offer_text: e.target.value
                  })}
                  placeholder="Your trial is ending soon! Would you like an extension?"
                  rows={2}
                  data-testid="extension-text-input"
                />
              </div>
              
              <div className="md:col-span-3 flex justify-end">
                <Button 
                  onClick={saveGlobalConfig} 
                  disabled={configSaving}
                  data-testid="save-config-btn"
                >
                  {configSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Configuration
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual Vendor Trial Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Individual Vendor Trial Management
          </CardTitle>
          <CardDescription>
            Extend or modify trial periods for specific vendors
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Search + List Section */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Filter vendors on trial..."
                  value={vendorSearch}
                  onChange={e => setVendorSearch(e.target.value)}
                  className="pl-10"
                  data-testid="vendor-trial-search"
                />
              </div>

              {/* Bulk action bar */}
              {checkedVendors.size > 0 && (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg">
                  <span className="text-sm text-blue-700 font-medium">
                    {checkedVendors.size} vendor(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCheckedVendors(new Set())}
                      className="text-blue-600 h-7"
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      className="h-7"
                      onClick={() => setShowExtendDialog(true)}
                      data-testid="bulk-extend-btn"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Extend Selected ({checkedVendors.size})
                    </Button>
                  </div>
                </div>
              )}

              <ScrollArea className="h-[300px] border rounded-lg">
                {vendorLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : vendors.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                    <Clock className="h-8 w-8 mb-2 text-gray-300" />
                    <p className="text-sm text-center">
                      {vendorSearch ? 'No matching vendors on trial' : 'No vendors currently on trial'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {vendors.map(vendor => (
                      <div
                        key={vendor.id}
                        className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${
                          selectedVendor?.id === vendor.id ? 'bg-blue-50' : ''
                        }`}
                        data-testid={`vendor-trial-${vendor.id}`}
                      >
                        <Checkbox
                          checked={checkedVendors.has(vendor.id)}
                          onCheckedChange={checked => {
                            setCheckedVendors(prev => {
                              const next = new Set(prev);
                              checked ? next.add(vendor.id) : next.delete(vendor.id);
                              return next;
                            });
                          }}
                          onClick={e => e.stopPropagation()}
                          data-testid={`check-${vendor.id}`}
                        />
                        <div
                          className="min-w-0 flex-1 flex items-center justify-between"
                          onClick={() => {
                            setSelectedVendor(vendor);
                            fetchVendorTrialStatus(vendor.id);
                          }}
                        >
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">
                              {vendor.store_name || vendor.name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {vendor.email}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant="outline" className="text-xs">Trial</Badge>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Trial Status Panel */}
            <div className="space-y-4">
              {!selectedVendor ? (
                <div className="flex flex-col items-center justify-center h-[356px] border rounded-lg bg-gray-50">
                  <Clock className="h-12 w-12 text-gray-300 mb-4" />
                  <p className="text-gray-500 text-sm">Select a vendor to manage their trial</p>
                </div>
              ) : trialStatus === null ? (
                <div className="flex items-center justify-center h-[356px] border rounded-lg">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <Card className="h-[356px]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">
                      {trialStatus.vendor?.name || selectedVendor.store_name}
                    </CardTitle>
                    <CardDescription>{trialStatus.vendor?.email}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!trialStatus.has_subscription ? (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg text-yellow-700">
                        <AlertTriangle className="h-5 w-5" />
                        <span className="text-sm">No subscription found</span>
                      </div>
                    ) : !trialStatus.on_trial ? (
                      <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                        <span className="text-sm">Vendor is not on trial</span>
                        <Badge>{trialStatus.plan_name || 'Active Plan'}</Badge>
                      </div>
                    ) : (
                      <>
                        {/* Trial Status */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-xs text-gray-500 mb-1">Trial Ends</div>
                            <div className="font-medium">
                              {trialStatus.trial_end 
                                ? new Date(trialStatus.trial_end).toLocaleDateString()
                                : 'N/A'
                              }
                            </div>
                          </div>
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <div className="text-xs text-blue-600 mb-1">Days Remaining</div>
                            <div className="font-bold text-blue-700 text-xl">
                              {trialStatus.days_remaining}
                            </div>
                          </div>
                        </div>

                        {/* Extension History */}
                        {trialStatus.trial_extended && (
                          <div className="bg-green-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-1">
                              <RefreshCw className="h-4 w-4" />
                              Previously Extended
                            </div>
                            <div className="text-xs text-green-600">
                              +{trialStatus.extension_history?.days || 0} days
                              {trialStatus.extension_history?.reason && (
                                <span className="block mt-1 italic">
                                  "{trialStatus.extension_history.reason}"
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Plan Info */}
                        {trialStatus.plan_name && (
                          <div className="text-sm text-gray-600">
                            Current Plan: <Badge variant="outline">{trialStatus.plan_name}</Badge>
                          </div>
                        )}

                        {/* Extend Button */}
                        <Button
                          className="w-full"
                          onClick={() => setShowExtendDialog(true)}
                          data-testid="extend-trial-btn"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Extend Trial
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Extend Trial Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Trial Period</DialogTitle>
            <DialogDescription>
              {checkedVendors.size >= 1
                ? `Extending trial for ${checkedVendors.size} vendor(s)`
                : `Extend trial for ${selectedVendor?.store_name || selectedVendor?.name}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Extension Days</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={extensionDays}
                onChange={e => setExtensionDays(parseInt(e.target.value) || 7)}
                data-testid="extension-days-input-modal"
              />
              <p className="text-xs text-gray-500">Maximum 90 days</p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason (Optional)</Label>
              <Textarea
                placeholder="e.g., Customer requested more time to evaluate..."
                value={extensionReason}
                onChange={e => setExtensionReason(e.target.value)}
                data-testid="extension-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExtendTrial} 
              disabled={extending || extensionDays < 1}
              data-testid="confirm-extend-btn"
            >
              {extending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Calendar className="h-4 w-4 mr-2" />
              )}
              Extend by {extensionDays} Days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrialSettingsTab;
